<template>
  <ContentWrap>
    <el-form ref="queryFormRef" :inline="true" :model="queryParams" class="-mb-15px" label-width="80px">
      <el-form-item label="酒店" prop="shopId">
        <el-select v-model="queryParams.shopId" placeholder="全部酒店" clearable filterable class="!w-220px">
          <el-option v-for="item in shopList" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
      </el-form-item>
      <el-form-item label="评分" prop="rating">
        <el-select v-model="queryParams.rating" placeholder="全部评分" clearable class="!w-140px">
          <el-option v-for="n in 5" :key="n" :label="`${n} 星及以上`" :value="n" />
        </el-select>
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-select v-model="queryParams.status" placeholder="全部状态" clearable class="!w-140px">
          <el-option label="待审核" :value="0" />
          <el-option label="已发布" :value="1" />
          <el-option label="已隐藏" :value="2" />
        </el-select>
      </el-form-item>
      <el-form-item label="评价内容" prop="content">
        <el-input
          v-model="queryParams.content"
          placeholder="关键字"
          clearable
          class="!w-180px"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column label="酒店" prop="hotelName" min-width="170" show-overflow-tooltip>
        <template #default="scope">{{ scope.row.hotelName || `酒店#${scope.row.shopId}` }}</template>
      </el-table-column>
      <el-table-column label="房型" prop="roomTypeName" min-width="130" show-overflow-tooltip>
        <template #default="scope">{{ scope.row.roomTypeName || '-' }}</template>
      </el-table-column>
      <el-table-column label="评分" width="150" align="center">
        <template #default="scope">
          <el-rate :model-value="scope.row.rating" disabled />
        </template>
      </el-table-column>
      <el-table-column label="评价内容" prop="content" min-width="220" show-overflow-tooltip />
      <el-table-column label="回复" prop="reply" min-width="150" show-overflow-tooltip>
        <template #default="scope">
          <el-tag v-if="scope.row.reply" type="success" size="small">已回复</el-tag>
          <el-tag v-else type="info" size="small">未回复</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="100" align="center">
        <template #default="scope">
          <el-tag v-if="scope.row.status === 0" type="info">待审核</el-tag>
          <el-tag v-else-if="scope.row.status === 1" type="success">已发布</el-tag>
          <el-tag v-else-if="scope.row.status === 2" type="danger">已隐藏</el-tag>
        </template>
      </el-table-column>
      <el-table-column
        :formatter="dateFormatter"
        label="创建时间"
        prop="createTime"
        width="180"
        align="center"
      />
      <el-table-column label="操作" width="180" fixed="right" align="center">
        <template #default="scope">
          <el-button
            v-if="scope.row.status !== 2"
            v-hasPermi="['hotel:platform:review:govern']"
            link
            type="danger"
            @click="changeStatus(scope.row, 2)"
          >
            隐藏
          </el-button>
          <el-button
            v-else
            v-hasPermi="['hotel:platform:review:govern']"
            link
            type="success"
            @click="changeStatus(scope.row, 1)"
          >
            恢复
          </el-button>
          <el-button
            v-hasPermi="['hotel:platform:review:govern']"
            link
            type="primary"
            @click="openReply(scope.row)"
          >
            回复
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <Dialog v-model="replyVisible" title="平台回复评价" width="520px">
    <el-form :model="replyForm" label-width="80px">
      <el-form-item label="评价内容">
        <span>{{ replyForm.content }}</span>
      </el-form-item>
      <el-form-item label="回复内容" required>
        <el-input
          v-model="replyForm.reply"
          :rows="4"
          type="textarea"
          maxlength="500"
          show-word-limit
          placeholder="请输入回复内容"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button :loading="replyLoading" type="primary" @click="submitReply">确定</el-button>
      <el-button @click="replyVisible = false">取消</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { dateFormatter } from '@/utils/formatTime'
import * as ReviewApi from '@/api/hotel/review'
import type { ReviewVO } from '@/api/hotel/review'
import { getShopSimpleList } from '@/api/hotel/platformShop'
import type { HotelPlatformShopOption } from '@/api/hotel/platformShop'

defineOptions({ name: 'HotelPlatformReview' })

const message = useMessage()
const loading = ref(true)
const list = ref<ReviewVO[]>([])
const total = ref(0)
const shopList = ref<HotelPlatformShopOption[]>([])
const queryFormRef = ref()

const queryParams = reactive({
  pageNo: 1,
  pageSize: 20,
  shopId: undefined as string | number | undefined,
  rating: undefined as number | undefined,
  status: undefined as number | undefined,
  content: undefined as string | undefined
})

const getList = async () => {
  loading.value = true
  try {
    const data = await ReviewApi.getReviewPage({
      ...queryParams,
      content: queryParams.content || undefined
    })
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value?.resetFields()
  queryParams.shopId = undefined
  queryParams.rating = undefined
  queryParams.status = undefined
  queryParams.content = undefined
  handleQuery()
}

// 隐藏 / 恢复
const changeStatus = async (row: ReviewVO, status: number) => {
  const action = status === 2 ? '隐藏' : '恢复'
  await message.confirm(`确认${action}该评价吗？`)
  await ReviewApi.updateReviewStatus(row.id!, status)
  message.success(`${action}成功`)
  await getList()
}

// 回复
const replyVisible = ref(false)
const replyLoading = ref(false)
const replyForm = reactive({ id: '' as string | number, content: '', reply: '' })

const openReply = (row: ReviewVO) => {
  replyForm.id = row.id!
  replyForm.content = row.content
  replyForm.reply = row.reply || ''
  replyVisible.value = true
}

const submitReply = async () => {
  if (!replyForm.reply.trim()) {
    message.warning('请输入回复内容')
    return
  }
  replyLoading.value = true
  try {
    await ReviewApi.replyReview(replyForm.id, replyForm.reply.trim())
    message.success('回复成功')
    replyVisible.value = false
    await getList()
  } finally {
    replyLoading.value = false
  }
}

onMounted(async () => {
  getList()
  shopList.value = (await getShopSimpleList()) || []
})
</script>
